import {
  TextInput,
  Select,
  Text,
  Avatar,
  Textarea,
  NumberInput,
  Switch,
  Badge,
  Tabs,
  Container,
  Title,
  Space,
} from "@mantine/core";
import {
  PageList,
  ENUM_FORM_DISPLAY,
  ENUM_FORM_LAYOUT_CONTAINER,
} from "../../components/pageList";
import ActionCellRender from "../../components/actionCellRender";
import { IconTrash, IconPencil, IconSettings } from "@tabler/icons";
import { DATA_AGE_TYPES, DATA_COUNTRY } from "../../data/options";
import moment from "moment";
import { DatePicker } from "@mantine/dates";
import RemoteSelect2 from "../../components/remoteSelect2";
import { useCellRender } from "../../hooks/useCellRender";
import { ImageThumbList } from "../../components/clientPaymentList";
import { renderCpdScoresCell } from "../../components/cpdScoresCell";
import { CPD_CREDIT_FIELDS } from "../../data/cpdFormFields";
import CpdFormCertificatePanel from "../../components/cpdFormCertificatePanel";
import CpdCreditsSummary from "../../components/cpdCreditsSummary";
import { useState } from "react";
import _ from "lodash";

const apiEntity = "cpd";

const formSchema = {
  title: "CPD & CPT",
  isMainForm: true,
  display: {
    mode: ENUM_FORM_DISPLAY.MODAL,
    size: "xl",
    position: "right",
  },
  closeAfterSave: true,
  apiEntity: "cpd",

  initialValues: {
    passApproval: true,
    files: [],
  },

  validate: {
    dateEnroll: (value) =>
      !_.isDate(value) ? "Please fill in the date" : null,
    consultant: (value) =>
      _.isEmpty(value) ? "Please select consultant" : null,
  },

  layout: {
    containers: [
      {
        key: "main-g",
        parent: null,
        type: ENUM_FORM_LAYOUT_CONTAINER.GRID,
        cols: [
          {
            key: "col-form",
            props: { span: 6 },
          },
          {
            key: "col-cert",
            props: { span: 6 },
          },
        ],
      },
    ],
    fields: [
      {
        name: "dateEnroll",
        type: "datePicker",
        component: DatePicker,
        parent: "col-form",
        props: {
          required: true,
          label: "Date",
          zIndex: 1000,
          placeholder: "Pick Date",
          allowFreeInput: true,
        },
      },

      {
        name: "consultant",
        component: RemoteSelect2,
        parent: "col-form",
        props: {
          label: "Consultant",
          apiEntity: "user",
          required: true,
          placeholder: "Please select consultant",
          valueField: "_id",
          labelField: "name",
          labelRender: (data) =>
            `${data.name} ${data.aliasName ? `, ${data.aliasName}` : ""} (${
              data.staffId ?? " -- "
            })`,
          onDataChange: (consultant, form) => {
            if (!consultant) form.setFieldValue("consultantInfo", {});
            else form.setFieldValue("consultantInfo", consultant);
          },
          searchFields: [
            "aliasName",
            "name",
            "staffId",
            "cname",
            "email",
            "phone",
          ],
          sort: {
            by: "name",
            order: "desc",
          },
        },
      },

      {
        name: "courseTitle",
        component: TextInput,
        parent: "col-form",
        props: {
          required: true,
          label: "Course Title",
          placeholder: "",
        },
      },

      {
        name: "courseCode",
        component: TextInput,
        parent: "col-form",
        props: {
          required: true,
          label: "Course Code",
          placeholder: "",
        },
      },
      {
        name: "passApproval",
        component: Switch,
        parent: "col-form",
        props: {
          type: "checkbox",
          label: "Approved",
          description: "Only approved records count toward consultant CPD totals",
        },
      },
      ...CPD_CREDIT_FIELDS.map((field) => ({
        name: field.name,
        component: NumberInput,
        parent: "col-form",
        props: {
          label: field.label,
          placeholder: "",
          precision: 1,
        },
      })),
      {
        name: "files",
        component: CpdFormCertificatePanel,
        parent: "col-cert",
        props: {},
      },
    ],
  },
};

function Cpd() {
  const [cellRender] = useCellRender();
  const [activeTab, setActiveTab] = useState("records");
  const [visitedTabs, setVisitedTabs] = useState(new Set(["records"]));

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setVisitedTabs((prev) => new Set([...prev, tab]));
  };

  const tableSchema = {
    showActionColumn: true,
    columns: [
      {
        field: "dateEnroll",
        sortable: true,
        headerName: "Course Date",
        cellRender: (col, data) => moment(data.dateEnroll).format("LL"),
      },
      {
        field: "consultant",
        sortable: true,
        headerName: "Consultant",
        cellRender: (col, data) => cellRender.User(data.consultant),
      },
      {
        field: "courseTitle",
        headerName: "Course",
        cellRender: (col, data) => (
          <>
            <Text size={"xs"} lineClamp={1} w={350}>
              {data.courseTitle}
            </Text>
            <Text size={"xs"} color="dimmed">
              {data.courseCode}
            </Text>
          </>
        ),
      },

      {
        field: "scores",
        headerName: "Scores",
        cellRender: renderCpdScoresCell,
      },

      {
        field: "passApproval",
        headerName: "Approval",
        cellRender: (col, data) =>
          data.passApproval === false ? (
            <Badge color="orange" variant="light">
              Pending
            </Badge>
          ) : (
            <Badge color="green" variant="light">
              Approved
            </Badge>
          ),
      },

      {
        field: "files",
        headerName: "Files",
        cellRender: (col, data) => ImageThumbList({ files: data.files }),
      },

      {
        field: "action",
        cellRender: ActionCellRender,
        isAction: true,
        actions: [
          {
            name: "edit",
            icon: IconPencil,
            props: {
              //   color:"red"
            },
          },
        ],
        actionMenu: [
          {
            name: "delete",
            label: "Delete data",
            icon: IconTrash,
            props: {
              color: "red",
            },
          },
        ],
      },
    ],
    searchableFields: [
      "courseTitle",
      "courseCode",
      "consultantInfo.name",
      "consultantInfo.cname",
      "consultantInfo.email",
      "consultantInfo.staffId",
      "consultantInfo.aliasName",
    ],
  };
  return (
    <Container size="xl">
      <Title align="left">CPD & CPT Records</Title>
      <Space h="xl" />
      <Tabs
        value={activeTab}
        onTabChange={handleTabChange}
        variant="outline"
      >
        <Tabs.List mb="md">
          <Tabs.Tab value="records">Records</Tabs.Tab>
          <Tabs.Tab value="credits-summary">Credits Summary</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="records">
          {visitedTabs.has("records") && (
            <PageList
              title=""
              apiEntity={apiEntity}
              tableSchema={tableSchema}
              formSchema={formSchema}
              showImport={true}
              showTemplateDownload={true}
              templateDownloadUrl={`${process.env.REACT_APP_SERVER_URL}/cpd/downloadTemplate`}
              templateDownloadText="Download Template"
            />
          )}
        </Tabs.Panel>

        <Tabs.Panel value="credits-summary">
          {visitedTabs.has("credits-summary") && <CpdCreditsSummary />}
        </Tabs.Panel>
      </Tabs>
    </Container>
  );
}

export default Cpd;
